import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  type Unsubscribe,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import type { AnalysisCohortSlice, AnalysisTabKey, AnalysisTimeRangeKey } from '../types/analysis'

export interface AnalysisSavedViewFilters {
  timeRange: AnalysisTimeRangeKey
  includeArchived: boolean
  includedSystems: string[]
  highlightAnomalies: boolean
}

export interface AnalysisSavedView {
  id: string
  workspaceId: string
  ownerUid: string
  name: string
  createdAt: Date
  updatedAt: Date
  tabKey: AnalysisTabKey
  filters: AnalysisSavedViewFilters
  slice: AnalysisCohortSlice | null
  isPreset?: boolean
}

export interface SaveAnalysisViewInput {
  name: string
  tabKey: AnalysisTabKey
  filters: AnalysisSavedViewFilters
  slice: AnalysisCohortSlice | null
}

export const DEFAULT_PRESET_VIEWS: Array<Omit<AnalysisSavedView, 'id' | 'workspaceId' | 'ownerUid' | 'createdAt' | 'updatedAt'> & { id: string }> = [
  {
    id: 'preset-ops-overview',
    name: 'Ops Overview',
    tabKey: 'OPERATIONS',
    filters: {
      timeRange: '7D',
      includeArchived: false,
      includedSystems: [],
      highlightAnomalies: false,
    },
    isPreset: true,
    slice: null,
  },
  {
    id: 'preset-team-performance',
    name: 'Team Performance',
    tabKey: 'TEAM',
    filters: {
      timeRange: '7D',
      includeArchived: false,
      includedSystems: [],
      highlightAnomalies: false,
    },
    isPreset: true,
    slice: null,
  },
  {
    id: 'preset-blocked-work',
    name: 'Blocked Work',
    tabKey: 'WORK',
    filters: {
      timeRange: '7D',
      includeArchived: false,
      includedSystems: [],
      highlightAnomalies: true,
    },
    isPreset: true,
    slice: null,
  },
]

export const listAnalysisSavedViews = async (workspaceId: string): Promise<AnalysisSavedView[]> => {
  if (!workspaceId) return []
  const viewsRef = collection(firestoreDb, 'workspaces', workspaceId, 'analytics_saved_views')
  const q = query(viewsRef, orderBy('createdAt', 'desc'))
  const snapshot = await getDocs(q)
  return snapshot.docs.map((docSnap) => {
    const data = docSnap.data()
    return {
      id: docSnap.id,
      workspaceId,
      ownerUid: data.ownerUid ?? '',
      name: data.name ?? 'Untitled View',
      createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
      updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(),
      tabKey: data.tabKey ?? 'OVERVIEW',
      filters: {
        timeRange: data.filters?.timeRange ?? '7D',
        includeArchived: Boolean(data.filters?.includeArchived),
        includedSystems: Array.isArray(data.filters?.includedSystems) ? data.filters.includedSystems : [],
        highlightAnomalies: Boolean(data.filters?.highlightAnomalies),
      },
      slice: data.slice?.kind === 'ROLE' || data.slice?.kind === 'ASSIGNEE_CLUSTER' ? { kind: data.slice.kind, value: String(data.slice.value) } : null,
      isPreset: docSnap.id.startsWith('preset-'),
    }
  })
}

export const subscribeAnalysisSavedViews = (
  workspaceId: string,
  onChange: (views: AnalysisSavedView[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe => {
  if (!workspaceId) {
    onChange([])
    return () => {}
  }

  const viewsRef = collection(firestoreDb, 'workspaces', workspaceId, 'analytics_saved_views')
  const q = query(viewsRef, orderBy('createdAt', 'desc'))

  return onSnapshot(
    q,
    (snapshot) => {
      const views: AnalysisSavedView[] = snapshot.docs.map((docSnap) => {
        const data = docSnap.data()
        return {
          id: docSnap.id,
          workspaceId,
          ownerUid: data.ownerUid ?? '',
          name: data.name ?? 'Untitled View',
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(),
          tabKey: data.tabKey ?? 'OVERVIEW',
          filters: {
            timeRange: data.filters?.timeRange ?? '7D',
            includeArchived: Boolean(data.filters?.includeArchived),
            includedSystems: Array.isArray(data.filters?.includedSystems) ? data.filters.includedSystems : [],
            highlightAnomalies: Boolean(data.filters?.highlightAnomalies),
          },
          slice: data.slice?.kind === 'ROLE' || data.slice?.kind === 'ASSIGNEE_CLUSTER' ? { kind: data.slice.kind, value: String(data.slice.value) } : null,
          isPreset: docSnap.id.startsWith('preset-'),
        }
      })
      onChange(views)
    },
    (err) => {
      onError?.(err)
    },
  )
}

export const saveAnalysisView = async (
  workspaceId: string,
  uid: string,
  input: SaveAnalysisViewInput,
): Promise<AnalysisSavedView> => {
  if (!workspaceId || !uid) throw new Error('Missing workspaceId or uid')
  const viewsRef = collection(firestoreDb, 'workspaces', workspaceId, 'analytics_saved_views')
  const newDocRef = doc(viewsRef)
  const now = new Date()

  const payload = {
    ownerUid: uid,
    name: input.name.trim() || 'Untitled View',
    tabKey: input.tabKey,
    filters: input.filters,
    slice: input.slice,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }

  await setDoc(newDocRef, payload)

  return {
    id: newDocRef.id,
    workspaceId,
    ownerUid: uid,
    name: payload.name,
    tabKey: payload.tabKey,
    filters: payload.filters,
    slice: payload.slice,
    createdAt: now,
    updatedAt: now,
    isPreset: false,
  }
}

export const updateAnalysisView = async (
  workspaceId: string,
  viewId: string,
  updates: Partial<SaveAnalysisViewInput>,
): Promise<void> => {
  if (!workspaceId || !viewId) throw new Error('Missing workspaceId or viewId')
  const docRef = doc(firestoreDb, 'workspaces', workspaceId, 'analytics_saved_views', viewId)
  const patch: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
  }
  if (updates.name !== undefined) patch.name = updates.name.trim()
  if (updates.tabKey !== undefined) patch.tabKey = updates.tabKey
  if (updates.filters !== undefined) patch.filters = updates.filters
  if (updates.slice !== undefined) patch.slice = updates.slice

  await updateDoc(docRef, patch)
}

export const deleteAnalysisView = async (
  workspaceId: string,
  viewId: string,
): Promise<void> => {
  if (!workspaceId || !viewId) throw new Error('Missing workspaceId or viewId')
  const docRef = doc(firestoreDb, 'workspaces', workspaceId, 'analytics_saved_views', viewId)
  await deleteDoc(docRef)
}

export const seedPresetViewsIfEmpty = async (
  workspaceId: string,
  uid: string,
  existingCount: number = 0,
): Promise<void> => {
  if (!workspaceId || !uid || existingCount > 0) return
  const viewsRef = collection(firestoreDb, 'workspaces', workspaceId, 'analytics_saved_views')
  try {
    const snap = await getDocs(viewsRef)
    if (!snap.empty) return

    for (const preset of DEFAULT_PRESET_VIEWS) {
      const docRef = doc(viewsRef, preset.id)
      await setDoc(docRef, {
        ownerUid: uid,
        name: preset.name,
        tabKey: preset.tabKey,
        filters: preset.filters,
        slice: preset.slice,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
    }
  } catch {
    // Non-blocking if offline or insufficient permissions
  }
}
